import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import {
  ReceiptText,
  FileText,
  Clock,
  Calendar,
  DollarSign,
  User,
  PlusCircle,
  Building2,
  PoundSterling,
  AlertCircle,
} from "lucide-react";
import DashboardContainer from "../layout/DashboardContainer";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";
import { useToast } from "@/components/ui/use-toast";
import { Currency } from "@/components/i18n";
import { useCountry } from "@/contexts/CountryContext";
import {
  ContractorEntity,
  GetContractorEntityResponse,
} from "@/types/contractor-entity";
import { GetInvoicesResponse } from "@/types/invoice";
import { ListContractorContractsResponse } from "@/types/contractor-agreement";
import expenseService from "@/services/expenseService";
import { ExpenseSummary } from "@/types/expense";

// Helper function to get the appropriate currency icon based on country context
const getCurrencyIcon = (currencyCode?: string): React.ReactNode => {
  switch (currencyCode) {
    case 'USD':
      return <DollarSign className="h-4 w-4 text-muted-foreground" />;
    case 'GBP':
    default:
      return <PoundSterling className="h-4 w-4 text-muted-foreground" />;
  }
};

const DashboardCard: React.FC<{
  title: string;
  value: string | number;
  subtitle: string;
  icon: React.ReactNode;
  link?: string;
}> = ({ title, value, subtitle, icon, link }) => {
  const { formatCurrency } = useCountry();
  const content = (
    <Card className="transition-all hover:shadow-md hover:scale-[1.02]">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <div className="h-8 w-8 rounded-full bg-secondary/10 flex items-center justify-center">
          {icon}
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">
          {typeof value === "number" && title.includes("Total") ? (
            <Currency amount={value} />
          ) : (
            value
          )}
        </div>
        <p className="text-xs text-muted-foreground">{subtitle}</p>
      </CardContent>
    </Card>
  );

  if (link) {
    return (
      <Link to={link} className="block no-underline">
        {content}
      </Link>
    );
  }

  return content;
};

export function ContractorDashboard() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { currentCountry } = useCountry();
  const [hasEntity, setHasEntity] = useState<boolean | null>(null);
  const [entity, setEntity] = useState<ContractorEntity | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [totalEarnings, setTotalEarnings] = useState<number>(0);
  const [pendingInvoices, setPendingInvoices] = useState<number>(0);
  const [activeContracts, setActiveContracts] = useState<number>(0);
  const [draftExpenses, setDraftExpenses] = useState<number>(0);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        // Fetch entity details
        const entityResponse =
          await ApiService.request<GetContractorEntityResponse>(
            EndpointKeys.GET_CONTRACTOR_ENTITY,
          );
        setHasEntity(entityResponse.hasEntity);
        if (entityResponse.entity) {
          setEntity(entityResponse.entity);
        }

        // Fetch invoices data for total earnings and pending invoices
        try {
          const invoicesResponse =
            await ApiService.request<GetInvoicesResponse>(
              EndpointKeys.GET_INVOICES,
            );

          // Calculate total earnings from approved invoices
          const approvedInvoices = invoicesResponse.invoices.filter(
            (invoice) => invoice.status === "APPROVED",
          );
          const totalEarningsAmount = approvedInvoices.reduce(
            (sum, invoice) =>
              sum + (parseFloat(invoice.grandTotal.toString()) || 0),
            0,
          );
          setTotalEarnings(totalEarningsAmount);

          // Count pending invoices (submitted but not yet approved)
          const pendingCount = invoicesResponse.invoices.filter(
            (invoice) => invoice.status === "SUBMITTED",
          ).length;
          setPendingInvoices(pendingCount);
        } catch (invoiceError) {
          console.error("Error fetching invoices:", invoiceError);
          // Don't show error toast for invoices as user might not have any yet
        }

        // Fetch contracts data for active contracts count
        try {
          const contractsResponse =
            await ApiService.request<ListContractorContractsResponse>(
              EndpointKeys.GET_CONTRACTOR_CONTRACTS,
            );

          if (contractsResponse.status === "success") {
            // Count active contracts
            const activeContractsCount = contractsResponse.contracts.filter(
              (contract) => contract.status === "ACTIVE",
            ).length;
            setActiveContracts(activeContractsCount);
          }
        } catch (contractError) {
          console.error("Error fetching contracts:", contractError);
          // Don't show error toast for contracts as user might not have any yet
        }

        // Fetch expense summary data for draft expenses
        try {
          const expenseSummary = await expenseService.getExpenseSummary();
          setDraftExpenses(expenseSummary.draftCount);
        } catch (expenseError) {
          console.error("Error fetching expense summary:", expenseError);
          // Don't show error toast for expenses as user might not have any yet
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        toast({
          title: "Error",
          description: "Failed to load dashboard data. Please try again.",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
  }, [toast]);

  if (isLoading) {
    return (
      <DashboardContainer title="Contractor Dashboard">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </DashboardContainer>
    );
  }

  return (
    <DashboardContainer title="Contractor Dashboard">
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Total Earnings
            </CardTitle>
            {getCurrencyIcon(currentCountry?.currencyCode)}
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              <Currency amount={totalEarnings} />
            </div>
            <p className="text-xs text-muted-foreground">
              From approved invoices
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Pending Invoices
            </CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{pendingInvoices}</div>
            <p className="text-xs text-muted-foreground">
              {pendingInvoices === 0
                ? "No pending invoices"
                : "Awaiting approval"}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Active Contracts
            </CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{activeContracts}</div>
            <p className="text-xs text-muted-foreground">
              {activeContracts === 0
                ? "No active contracts"
                : "Currently active"}
            </p>
          </CardContent>
        </Card>
        <Card className={`${draftExpenses > 0 ? 'border-destructive bg-destructive/5' : ''}`}>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Draft Expenses
            </CardTitle>
            <AlertCircle className={`h-4 w-4 ${draftExpenses > 0 ? 'text-destructive' : 'text-muted-foreground'}`} />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${draftExpenses > 0 ? 'text-destructive' : ''}`}>
              {draftExpenses}
            </div>
            <p className={`text-xs ${draftExpenses > 0 ? 'text-destructive/70' : 'text-muted-foreground'}`}>
              {draftExpenses === 0
                ? "No pending expenses"
                : "Need to be submitted"}
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="mt-8">
        <h2 className="text-lg font-semibold mb-4">Quick Actions</h2>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {!hasEntity && (
            <Card>
              <CardContent
                className="p-6 cursor-pointer"
                onClick={() => navigate("/contractor/profile")}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-medium">Business Details</h3>
                    <p className="text-sm text-muted-foreground mt-1">
                      Set up your business entity details
                    </p>
                  </div>
                  <Link to="/contractor/profile">
                    <Button size="icon" variant="ghost">
                      <Building2 className="h-5 w-5" />
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          )}
          <Card className={!hasEntity ? "opacity-50" : ""}>
            <CardContent
              className={`p-6 ${hasEntity ? "cursor-pointer" : "cursor-not-allowed"}`}
              onClick={() => hasEntity && navigate("/contractor/invoices")}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium">Create Invoice</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {hasEntity
                      ? "Create a new invoice for your services"
                      : "Complete business details first"}
                  </p>
                </div>
                <Button
                  size="icon"
                  variant="ghost"
                  disabled={!hasEntity}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (hasEntity) navigate("/contractor/invoices");
                  }}
                >
                  <PlusCircle className="h-5 w-5" />
                </Button>
              </div>
            </CardContent>
          </Card>
          <Card className={!hasEntity ? "opacity-50" : ""}>
            <CardContent
              className={`p-6 ${hasEntity ? "cursor-pointer" : "cursor-not-allowed"}`}
              onClick={() => hasEntity && navigate("/contractor/contracts")}
            >
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-medium">Manage Contracts</h3>
                  <p className="text-sm text-muted-foreground mt-1">
                    {hasEntity
                      ? "View and manage your service contracts"
                      : "Complete business details first"}
                  </p>
                </div>
                <Button
                  size="icon"
                  variant="ghost"
                  disabled={!hasEntity}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (hasEntity) navigate("/contractor/contracts");
                  }}
                >
                  <FileText className="h-5 w-5" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardContainer>
  );
}

export default ContractorDashboard;
